import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toasterApi } from '@terra/ui-design-system';
import type { FeedbackFailureArea } from '@terra/shared-contracts-signal-feedback';
import {
  type SignalStageType,
  type SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useAppTargetId } from '../../store/projectStore';
import { useUserTenants } from '../../hooks/api/useUserTenants';
import { useTranslations } from 'next-intl';
import { FINDINGS_BY_SIGNAL_ID_QUERY_KEY } from '../../hooks/api/useFindingsBySignalId';
import { MENTIONS_FINDINGS_QUERY_KEY } from '../../hooks/api/useMentionsFindings';
import { MENTIONS_SIGNALS_QUERY_KEY } from '../../hooks/api/useMentionsSignals';
import { TARGET_SIGNALS_QUERY_KEY } from '../../hooks/api/useTargetSignals';
import {
  isFailureAreaStatusChange,
  resolveReportFalsePositive,
  resolveStageForStatusUpdate,
  shouldConfirmDeleteRelatedFinding,
  type SignalRelatedFindingRef,
} from './signalStatusRelatedFinding';

export type PendingStatusUpdate = {
  newStatus: SignalStatusType | null;
  note?: string;
  isFalsePositive?: boolean;
  failureArea?: FeedbackFailureArea;
};

export const RELATED_FINDING_CONFIRM_DECISION = {
  Delete: 'delete',
  Keep: 'keep',
} as const;

export type RelatedFindingConfirmDecision =
  (typeof RELATED_FINDING_CONFIRM_DECISION)[keyof typeof RELATED_FINDING_CONFIRM_DECISION];

export function useSignalStatusUpdate({
  signalId,
  currentStage,
  currentStatus,
  relatedFinding,
  onStatusChange,
}: {
  signalId: string;
  currentStage: SignalStageType;
  currentStatus: SignalStatusType | null;
  relatedFinding?: SignalRelatedFindingRef;
  onStatusChange?: ({
    stage,
    status,
  }: {
    stage: SignalStageType;
    status: SignalStatusType | null;
  }) => void;
}) {
  const queryClient = useQueryClient();
  const appTargetId = useAppTargetId();
  const { hasAccessToTerraTenant } = useUserTenants();
  const t = useTranslations('signals.signalStatusSelector');
  const tSignalStatus = useTranslations(
    'designSystem.signals.lifecycle.status',
  );
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const pendingStatusUpdateRef = useRef<PendingStatusUpdate | null>(null);

  const getStatusDisplayText = (status: SignalStatusType | null) =>
    status === null ? t('unclassified') : tSignalStatus(status);

  const invalidateRelatedFindingCaches = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [TARGET_SIGNALS_QUERY_KEY],
        exact: false,
      }),
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === FINDINGS_BY_SIGNAL_ID_QUERY_KEY &&
          query.queryKey.includes(signalId),
      }),
      queryClient.invalidateQueries({
        queryKey: [MENTIONS_FINDINGS_QUERY_KEY],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: [MENTIONS_SIGNALS_QUERY_KEY],
        exact: false,
      }),
    ]);
  };

  const performStatusUpdate = async ({
    newStatus,
    note,
    isFalsePositive,
    failureArea,
  }: PendingStatusUpdate): Promise<{ showSuccessToast: boolean }> => {
    const previousStage = currentStage;
    const previousStatus = currentStatus;
    const newStage = resolveStageForStatusUpdate({
      currentStage,
      newStatus,
    });

    try {
      onStatusChange?.({ stage: newStage, status: newStatus });

      const updatedSignal = await terraProxyApiClient.updateSignal(signalId, {
        stage: newStage,
        status: newStatus,
        ...(note && { note }),
      });

      const actualStage = updatedSignal.stage;
      const actualStatus = updatedSignal.status;

      if (actualStage !== newStage) {
        onStatusChange?.({
          stage: actualStage,
          status: actualStatus,
        });
      }

      const capturesFailureArea = isFailureAreaStatusChange({
        previousStatus,
        newStatus: actualStatus,
      });
      const shouldSubmitFeedback =
        appTargetId &&
        (note ||
          isFalsePositive ||
          (hasAccessToTerraTenant && failureArea && capturesFailureArea));
      let feedbackSubmitFailed = false;

      if (shouldSubmitFeedback) {
        try {
          await terraProxyApiClient.submitSignalFeedback({
            applicationId: appTargetId,
            signalId,
            previousStage,
            previousStatus,
            newStage: actualStage,
            newStatus: actualStatus,
            ...(note && { feedback: note }),
            ...(hasAccessToTerraTenant && failureArea && capturesFailureArea
              ? { failureArea }
              : {}),
            isFalsePositive,
          });
        } catch (feedbackError) {
          feedbackSubmitFailed = true;
          console.error(
            'Failed to submit signal feedback event:',
            feedbackError,
          );
          toasterApi.error({
            title: t('feedbackSubmitFailedTitle'),
            description: t('feedbackSubmitFailedDescription'),
          });
        }
      }

      return { showSuccessToast: !feedbackSubmitFailed };
    } catch (error) {
      onStatusChange?.({ stage: previousStage, status: previousStatus });

      console.error('Failed to update signal status:', error);
      toasterApi.error({
        title: t('statusUpdateFailedTitle'),
        description: t('statusUpdateFailedDescription'),
      });

      throw error;
    }
  };

  const finishStatusUpdate = async ({
    update,
    deleteRelatedFinding,
  }: {
    update: PendingStatusUpdate;
    deleteRelatedFinding: boolean;
  }): Promise<void> => {
    setIsUpdatingStatus(true);

    try {
      const { showSuccessToast } = await performStatusUpdate(update);
      let relatedFindingDeleteFailed = false;

      if (deleteRelatedFinding && relatedFinding) {
        try {
          await terraProxyApiClient.deleteFinding(relatedFinding.id);
          await invalidateRelatedFindingCaches();
        } catch (deleteError) {
          relatedFindingDeleteFailed = true;
          console.error('Failed to delete related finding:', deleteError);
          toasterApi.error({
            title: t('deleteRelatedFindingFailedTitle'),
            description: t('deleteRelatedFindingFailedDescription'),
          });
        }
      }

      if (showSuccessToast && !relatedFindingDeleteFailed) {
        toasterApi.success({
          title: t('statusUpdatedTitle'),
          description: t('statusUpdatedDescription', {
            status: getStatusDisplayText(update.newStatus),
          }),
        });
      }
    } catch (error) {
      console.error('Failed to submit signal status update:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const requestStatusUpdate = async (
    update: PendingStatusUpdate,
  ): Promise<'pending-delete-confirm' | 'completed'> => {
    if (
      shouldConfirmDeleteRelatedFinding({
        newStatus: update.newStatus,
        relatedFinding,
      })
    ) {
      pendingStatusUpdateRef.current = update;
      setIsDeleteConfirmOpen(true);

      return 'pending-delete-confirm';
    }

    await finishStatusUpdate({ update, deleteRelatedFinding: false });

    return 'completed';
  };

  const resolveRelatedFindingConfirm = async (
    decision: RelatedFindingConfirmDecision,
  ): Promise<void> => {
    const update = pendingStatusUpdateRef.current;

    pendingStatusUpdateRef.current = null;
    setIsDeleteConfirmOpen(false);

    if (!update) {
      return;
    }

    await finishStatusUpdate({
      update,
      deleteRelatedFinding:
        decision === RELATED_FINDING_CONFIRM_DECISION.Delete,
    });
  };

  const buildFalsePositiveFlag = ({
    pendingStatus,
    markedAsFalsePositive,
  }: {
    pendingStatus: SignalStatusType | null | undefined;
    markedAsFalsePositive: boolean;
  }) =>
    resolveReportFalsePositive({
      currentStatus,
      pendingStatus,
      markedAsFalsePositive,
    });

  return {
    isUpdatingStatus,
    isDeleteConfirmOpen,
    getStatusDisplayText,
    requestStatusUpdate,
    resolveRelatedFindingConfirm,
    buildFalsePositiveFlag,
  };
}
